// Procurement > Waste Log — replaces Carman's "Waste Cost 2026.xlsx".
//
// Her workbook was a tab per month (one row per day: amount lost + a comment) and a
// "YTD" tab that summed the twelve. This page keeps both of those views — the month
// strip across the top IS the YTD tab, the table under it IS the month tab — and adds
// what the comment column could not do: every thing thrown out is its own line with a
// product, a quantity, a cost and a reason, so the year can be read by product.
//
// ⚠️ AMOUNT IS HERS TO SET. It pre-fills as quantity × cost per unit, and stops
// following the moment she types in it. Some of her real entries do not equal
// quantity × the usual cost, and the log has to be able to hold what she actually
// wrote off. See lib/waste-log.js.

import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Plus, Pencil, Trash2, Download, X, Mail } from 'lucide-react';
import { neonQuery } from '../services/api';
import { getCurrentUser } from '../services/auth';

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const MON = MONTHS.map((m) => m.slice(0, 3));
const DOW = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const UNITS = [
  ['bu', 'bushel'], ['dz', 'dozen'], ['lb', 'lb'], ['pint', 'pint'],
  ['bottle', 'bottle'], ['bag', 'bag'], ['case', 'case'], ['ea', 'each'],
];
const REASONS = ['Expired', 'Arrived spoiled', 'Damaged', 'Other'];
const REASON_COLOR = { Expired: '#00395C', 'Arrived spoiled': '#DC582A', Damaged: '#b45309', Other: '#5f6368' };

const money = (n) => '$' + Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const round2 = (n) => Math.round(n * 100) / 100;
const qtyText = (r) => (r.quantity == null ? '' : `${Number(r.quantity)} ${r.unit || ''}`.trim());

// Dates travel as 'YYYY-MM-DD' strings and are never run through new Date(string),
// which would read them as UTC and show the day before in Maryland.
const parts = (ymd) => ymd.split('-').map(Number);
const dayLabel = (ymd) => {
  const [y, m, d] = parts(ymd);
  return `${DOW[new Date(y, m - 1, d).getDay()]}, ${MON[m - 1]} ${d}`;
};
const todayYmd = () => {
  const n = new Date();
  return `${n.getFullYear()}-${String(n.getMonth() + 1).padStart(2, '0')}-${String(n.getDate()).padStart(2, '0')}`;
};

// "Email this entry" opens a draft in her own mail program — nothing is sent from
// here. She reads it over and presses Send herself, which is what she asked for.
const NOTIFY_TO = ['greg@crabplace.com', 'matt@crabplace.com', 'matt_jr@crabplace.com'];
const longDate = (ymd) => {
  const [y, m, d] = parts(ymd);
  return `${MON[m - 1]} ${d}, ${y}`;
};
// Outlook wants CRLF between lines; a bare line feed runs them together.
const mailto = (subject, lines) =>
  `mailto:${NOTIFY_TO.join(',')}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(lines.join('\r\n'))}`;
const entryLines = (r) => [
  `Item: ${r.product}`,
  `Quantity: ${qtyText(r) || '-'}`,
  r.unit_cost == null ? null : `Cost per unit: ${money(r.unit_cost)}/${r.unit || 'ea'}`,
  `Amount lost: ${money(r.amount)}`,
  `Reason: ${r.reason}`,
  `Logged by: ${r.created_by || '-'}`,
  `Notes: ${r.notes || '-'}`,
].filter(Boolean);
const mailtoFor = (r) => mailto(
  `Waste Log Entry – ${r.product} – ${longDate(r.waste_date)}`,
  [`Date: ${dayLabel(r.waste_date)}, ${parts(r.waste_date)[0]}`, ...entryLines(r)]
);
// One email for everything thrown out on a day. Carman, Oct 14, 2026: "if we throw
// out a few things on the same day I can send it all together."
const mailtoForDay = (dayRows) => {
  const ymd = dayRows[0].waste_date;
  const total = dayRows.reduce((s, r) => s + r.amount, 0);
  const lines = [`Date: ${dayLabel(ymd)}, ${parts(ymd)[0]}`, `Items: ${dayRows.length}`, `Total lost: ${money(total)}`];
  dayRows.forEach((r, i) => { lines.push('', `${i + 1}. ${r.product}`, ...entryLines(r).slice(1)); });
  return mailto(`Waste Log – ${longDate(ymd)} – ${dayRows.length} items – ${money(total)}`, lines);
};

const blankForm = (date) => ({
  id: null, waste_date: date || todayYmd(), product: '', quantity: '', unit: 'bu',
  unit_cost: '', amount: '', reason: 'Expired', notes: '',
});

function Bars({ title, rows, total, colorFor }) {
  const max = Math.max(...rows.map((r) => r.amount), 1);
  return (
    <div className="card" style={{ flex: '1 1 320px', minWidth: 0 }}>
      <div className="card-header">{title}</div>
      <div className="card-body">
        {!rows.length ? <div style={{ color: 'var(--gray-500)', fontSize: 12 }}>Nothing logged yet.</div> : null}
        {rows.map((r) => (
          <div key={r.label} style={{ marginBottom: 10 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, marginBottom: 3 }}>
              <span style={{ fontWeight: 600, color: 'var(--gray-900)' }}>
                {r.label}
                {r.detail ? <span style={{ fontWeight: 400, color: 'var(--gray-600)' }}> · {r.detail}</span> : null}
              </span>
              <span style={{ fontVariantNumeric: 'tabular-nums' }}>
                {money(r.amount)}
                <span style={{ color: 'var(--gray-600)' }}> · {total ? Math.round((r.amount / total) * 100) : 0}%</span>
              </span>
            </div>
            <div style={{ height: 8, background: 'var(--gray-100)', borderRadius: 4 }}>
              <div
                style={{
                  width: `${Math.max((r.amount / max) * 100, 1)}%`, height: 8, borderRadius: 4,
                  background: colorFor ? colorFor(r.label) : 'var(--navy)',
                }}
              />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function WasteLog() {
  const thisYear = new Date().getFullYear();
  const [year, setYear] = useState(thisYear);
  const [month, setMonth] = useState(new Date().getMonth()); // 0-11
  const [rows, setRows] = useState([]);
  const [known, setKnown] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [form, setForm] = useState(blankForm());
  const [amountTyped, setAmountTyped] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState('');
  const [savedRow, setSavedRow] = useState(null); // the entry just saved, for "Email this entry"
  const productRef = useRef(null);

  const load = useCallback(async () => {
    setErr('');
    try {
      const [list, products] = await Promise.all([
        neonQuery('waste_log_list', { year }),
        neonQuery('waste_log_products'),
      ]);
      setRows(list || []);
      setKnown(products || []);
    } catch (e) {
      setErr(e.message);
    } finally {
      setLoading(false);
    }
  }, [year]);

  useEffect(() => { setLoading(true); load(); }, [load]);

  const knownByName = useMemo(() => {
    const m = new Map();
    known.forEach((k) => m.set(k.product.toLowerCase(), k));
    return m;
  }, [known]);

  const byMonth = useMemo(() => {
    const out = MONTHS.map(() => ({ total: 0, count: 0 }));
    rows.forEach((r) => {
      const m = parts(r.waste_date)[1] - 1;
      out[m].total += r.amount;
      out[m].count += 1;
    });
    return out;
  }, [rows]);

  const ytd = useMemo(() => rows.reduce((s, r) => s + r.amount, 0), [rows]);
  const monthRows = useMemo(() => rows.filter((r) => parts(r.waste_date)[1] - 1 === month), [rows, month]);

  const byProduct = useMemo(() => {
    const m = new Map();
    rows.forEach((r) => {
      const k = r.product;
      const cur = m.get(k) || { label: k, amount: 0, qty: 0, unit: r.unit, mixed: false };
      cur.amount += r.amount;
      if (r.quantity != null) {
        if (cur.unit && r.unit && cur.unit !== r.unit) cur.mixed = true;
        cur.qty += Number(r.quantity);
      }
      m.set(k, cur);
    });
    return [...m.values()]
      .map((p) => ({ ...p, detail: p.qty && !p.mixed ? `${round2(p.qty)} ${p.unit || ''}`.trim() : '' }))
      .sort((a, b) => b.amount - a.amount);
  }, [rows]);

  const byReason = useMemo(() => {
    const m = new Map();
    rows.forEach((r) => m.set(r.reason, (m.get(r.reason) || 0) + r.amount));
    return [...m.entries()].map(([label, amount]) => ({ label, amount })).sort((a, b) => b.amount - a.amount);
  }, [rows]);

  // Quantity and cost drive the amount until she types an amount of her own.
  const patch = (field, value) => {
    setSaved('');
    setSavedRow(null);
    setForm((f) => {
      const next = { ...f, [field]: value };
      if ((field === 'quantity' || field === 'unit_cost') && !amountTyped) {
        const q = Number(next.quantity);
        const c = Number(next.unit_cost);
        next.amount = next.quantity !== '' && next.unit_cost !== '' && !Number.isNaN(q * c) ? String(round2(q * c)) : '';
      }
      return next;
    });
  };

  // A product she has logged before brings its unit and its last cost with it.
  const applyKnown = (name) => {
    const k = knownByName.get(String(name).trim().toLowerCase());
    if (!k) return;
    setForm((f) => {
      const next = { ...f, product: k.product, unit: k.unit || f.unit };
      if (f.unit_cost === '' && k.unit_cost != null) {
        next.unit_cost = String(k.unit_cost);
        if (!amountTyped && f.quantity !== '') next.amount = String(round2(Number(f.quantity) * k.unit_cost));
      }
      return next;
    });
  };

  const reset = (keepDate) => {
    setForm(blankForm(keepDate));
    setAmountTyped(false);
  };

  const save = async (e) => {
    e.preventDefault();
    if (!form.product.trim()) { setErr('Pick or type the product that was thrown out.'); return; }
    if (form.amount === '' || Number.isNaN(Number(form.amount))) { setErr('Enter the amount lost.'); return; }
    setSaving(true);
    setErr('');
    try {
      const wasEdit = !!form.id;
      const out = await neonQuery('waste_log_upsert', { ...form, by: getCurrentUser()?.staffName || '' });
      setSavedRow(Array.isArray(out) && out[0] ? out[0] : null);
      const [y, m] = parts(form.waste_date);
      setSaved(`${wasEdit ? 'Updated' : 'Logged'} ${form.product.trim()} — ${money(form.amount)}`);
      // Stay on the same date: one day's waste is usually several products.
      reset(form.waste_date);
      if (y !== year) setYear(y); else await load();
      setMonth(m - 1);
      if (productRef.current) productRef.current.focus();
    } catch (ex) {
      setErr(ex.message);
    } finally {
      setSaving(false);
    }
  };

  const edit = (r) => {
    setSaved('');
    setSavedRow(null);
    setErr('');
    setAmountTyped(true); // an existing amount is never silently recalculated
    setForm({
      id: r.id, waste_date: r.waste_date, product: r.product,
      quantity: r.quantity == null ? '' : String(r.quantity), unit: r.unit || 'ea',
      unit_cost: r.unit_cost == null ? '' : String(r.unit_cost), amount: String(r.amount),
      reason: r.reason, notes: r.notes || '',
    });
    window.scrollTo({ top: 0, behavior: 'smooth' });
    const pc = document.querySelector('.page-content');
    if (pc) pc.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const remove = async (r) => {
    if (!window.confirm(`Remove ${r.product} (${money(r.amount)}) from ${dayLabel(r.waste_date)}?`)) return;
    try {
      await neonQuery('waste_log_delete', { id: r.id, by: getCurrentUser()?.staffName || '' });
      if (form.id === r.id) reset();
      await load();
    } catch (ex) {
      setErr(ex.message);
    }
  };

  const exportCsv = () => {
    const q = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;
    const lines = [['Date', 'Product', 'Quantity', 'Unit', 'Cost per unit', 'Amount lost', 'Reason', 'Notes', 'Logged by'].join(',')];
    rows.forEach((r) => {
      const [y, m, d] = parts(r.waste_date);
      lines.push([`${m}/${d}/${y}`, r.product, r.quantity ?? '', r.unit, r.unit_cost ?? '', r.amount, r.reason, r.notes, r.created_by].map(q).join(','));
    });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(new Blob([lines.join('\r\n')], { type: 'text/csv' }));
    a.download = `Waste Log ${year}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  const years = [];
  for (let y = thisYear; y >= 2026; y--) years.push(y);
  const lbl = { display: 'block', fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5, color: 'var(--gray-700)', marginBottom: 4 };
  const th = { textAlign: 'left', padding: '8px 10px', fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.5, color: 'var(--gray-700)', borderBottom: '2px solid var(--gray-200)', whiteSpace: 'nowrap' };
  const td = { padding: '8px 10px', borderBottom: '1px solid var(--gray-100)', verticalAlign: 'top' };
  const num = { textAlign: 'right', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' };
  const monthTotal = byMonth[month].total;
  const savedDay = savedRow ? rows.filter((x) => x.waste_date === savedRow.waste_date) : [];

  return (
    <div>
      <div className="breadcrumb">
        <a href="#procurement">Procurement</a>
        <span> / </span>
        <span>Waste Log</span>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16, marginBottom: 16, flexWrap: 'wrap' }}>
        <div>
          <h1 style={{ margin: 0, color: 'var(--navy)' }}>Waste Log</h1>
          <div style={{ color: '#666', fontSize: 13, marginTop: 4 }}>
            Product thrown out — what it was, how much, what it cost and why.
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <select className="form-select" value={year} onChange={(e) => setYear(Number(e.target.value))} aria-label="Year">
            {years.map((y) => <option key={y} value={y}>{y}</option>)}
          </select>
          <button className="btn btn-outline" onClick={exportCsv} disabled={!rows.length}>
            <Download size={14} style={{ marginRight: 6 }} />Export {year}
          </button>
        </div>
      </div>

      {err ? (
        <div className="card" style={{ marginBottom: 16, borderLeft: '4px solid var(--orange)' }}>
          <div className="card-body" style={{ color: 'var(--orange)' }}>{err}</div>
        </div>
      ) : null}

      {/* ── Log waste ─────────────────────────────────────────────────────── */}
      <form className="card" style={{ marginBottom: 16, borderTop: '3px solid var(--orange)' }} onSubmit={save}>
        <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span>{form.id ? 'Edit entry' : 'Log waste'}</span>
          {saved ? (
            <span style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 12, fontWeight: 600, color: '#15803d', textTransform: 'none' }}>
              ✓ {saved}
              {savedRow ? (
                <a className="btn btn-outline" href={mailtoFor(savedRow)} style={{ textDecoration: 'none' }}>
                  <Mail size={14} style={{ marginRight: 6 }} />Email this entry
                </a>
              ) : null}
              {savedDay.length > 1 ? (
                <a className="btn btn-outline" href={mailtoForDay(savedDay)} style={{ textDecoration: 'none' }}>
                  <Mail size={14} style={{ marginRight: 6 }} />Email the whole day ({savedDay.length})
                </a>
              ) : null}
            </span>
          ) : null}
        </div>
        <div className="card-body">
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-end' }}>
            <div style={{ flex: '0 0 150px' }}>
              <label style={lbl} htmlFor="wl-date">Date</label>
              <input id="wl-date" type="date" className="form-input" value={form.waste_date} onChange={(e) => patch('waste_date', e.target.value)} required />
            </div>
            <div style={{ flex: '2 1 220px' }}>
              <label style={lbl} htmlFor="wl-product">Product</label>
              <input
                id="wl-product" ref={productRef} className="form-input" list="wl-products" autoComplete="off"
                placeholder="Regular females, clams, cocktail sauce…"
                value={form.product}
                onChange={(e) => { patch('product', e.target.value); if (knownByName.has(e.target.value.trim().toLowerCase())) applyKnown(e.target.value); }}
                onBlur={(e) => applyKnown(e.target.value)}
              />
              <datalist id="wl-products">
                {known.map((k) => <option key={k.product} value={k.product} />)}
              </datalist>
            </div>
            <div style={{ flex: '0 0 90px' }}>
              <label style={lbl} htmlFor="wl-qty">Quantity</label>
              <input id="wl-qty" type="number" step="any" min="0" className="form-input" value={form.quantity} onChange={(e) => patch('quantity', e.target.value)} />
            </div>
            <div style={{ flex: '0 0 105px' }}>
              <label style={lbl} htmlFor="wl-unit">Unit</label>
              <select id="wl-unit" className="form-select" style={{ width: '100%' }} value={form.unit} onChange={(e) => patch('unit', e.target.value)}>
                {UNITS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            </div>
            <div style={{ flex: '0 0 115px' }}>
              <label style={lbl} htmlFor="wl-cost">Cost per {form.unit === 'ea' ? 'unit' : form.unit}</label>
              <input id="wl-cost" type="number" step="any" min="0" className="form-input" value={form.unit_cost} onChange={(e) => patch('unit_cost', e.target.value)} />
            </div>
            <div style={{ flex: '0 0 120px' }}>
              <label style={lbl} htmlFor="wl-amount">Amount lost</label>
              <input
                id="wl-amount" type="number" step="any" min="0" className="form-input" placeholder="0.00"
                style={{ fontWeight: form.amount === '' ? 400 : 700 }} value={form.amount}
                onChange={(e) => { setAmountTyped(e.target.value !== ''); patch('amount', e.target.value); }}
              />
            </div>
            <div style={{ flex: '0 0 150px' }}>
              <label style={lbl} htmlFor="wl-reason">Reason</label>
              <select id="wl-reason" className="form-select" style={{ width: '100%' }} value={form.reason} onChange={(e) => patch('reason', e.target.value)}>
                {REASONS.concat(REASONS.includes(form.reason) ? [] : [form.reason]).map((r) => <option key={r} value={r}>{r}</option>)}
              </select>
            </div>
          </div>
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-end', marginTop: 12 }}>
            <div style={{ flex: '1 1 300px' }}>
              <label style={lbl} htmlFor="wl-notes">Notes (optional)</label>
              <input id="wl-notes" className="form-input" placeholder="Expired 1/6, arrived warm, lot number…" value={form.notes} onChange={(e) => patch('notes', e.target.value)} />
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              {form.id ? (
                <button type="button" className="btn btn-outline" onClick={() => reset()}>
                  <X size={14} style={{ marginRight: 4 }} />Cancel
                </button>
              ) : null}
              <button type="submit" className="btn btn-primary" disabled={saving}>
                {form.id ? null : <Plus size={15} style={{ marginRight: 4 }} />}
                {saving ? 'Saving…' : form.id ? 'Save changes' : 'Add to log'}
              </button>
            </div>
          </div>
          {!form.id ? (
            <div style={{ fontSize: 12, color: 'var(--gray-600)', marginTop: 10 }}>
              Threw out more than one thing that day? Add each one — the date stays put. Amount fills in from
              quantity × cost; type over it if the real loss was different.
            </div>
          ) : null}
        </div>
      </form>

      {/* ── The year, month by month (her "YTD" tab) ──────────────────────── */}
      <div className="card" style={{ marginBottom: 16 }}>
        <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span>{year} by month</span>
          <span style={{ fontSize: 13, textTransform: 'none' }}>
            Year to date <strong style={{ color: 'var(--navy)', fontSize: 15, marginLeft: 6 }}>{money(ytd)}</strong>
          </span>
        </div>
        <div className="card-body" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(92px, 1fr))', gap: 8 }}>
          {MONTHS.map((m, i) => {
            const on = i === month;
            const b = byMonth[i];
            return (
              <button
                key={m} type="button" onClick={() => setMonth(i)} aria-pressed={on}
                style={{
                  textAlign: 'left', padding: 8, borderRadius: 6, cursor: 'pointer',
                  fontFamily: 'inherit',
                  border: on ? '2px solid var(--orange)' : '1px solid var(--gray-200)',
                  background: on ? '#fdf1ec' : 'white',
                }}
              >
                <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, color: 'var(--gray-700)' }}>{MON[i]}</div>
                <div style={{ fontSize: 14, fontWeight: 700, color: b.total ? 'var(--navy)' : 'var(--gray-500)', fontVariantNumeric: 'tabular-nums' }}>
                  {b.total ? money(b.total) : '—'}
                </div>
                <div style={{ fontSize: 11, color: 'var(--gray-600)' }}>{b.count ? `${b.count} item${b.count === 1 ? '' : 's'}` : 'nothing logged'}</div>
              </button>
            );
          })}
        </div>
      </div>

      {/* ── The month (her monthly tab) ───────────────────────────────────── */}
      <div className="card" style={{ marginBottom: 16 }}>
        <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span>{MONTHS[month]} {year}</span>
          <span style={{ fontSize: 13, textTransform: 'none' }}>
            Month total <strong style={{ color: 'var(--navy)', fontSize: 15, marginLeft: 6 }}>{money(monthTotal)}</strong>
          </span>
        </div>
        {loading ? (
          <div className="card-body" style={{ color: 'var(--gray-600)', fontSize: 13 }}>Loading…</div>
        ) : !monthRows.length ? (
          <div className="card-body" style={{ color: 'var(--gray-600)', fontSize: 13 }}>
            Nothing logged for {MONTHS[month]} {year}.
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', fontSize: 12, borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  <th style={th}>Date</th>
                  <th style={th}>Product</th>
                  <th style={{ ...th, textAlign: 'right' }}>Quantity</th>
                  <th style={{ ...th, textAlign: 'right' }}>Cost per unit</th>
                  <th style={{ ...th, textAlign: 'right' }}>Amount lost</th>
                  <th style={th}>Reason</th>
                  <th style={th}>Notes</th>
                  <th style={th}>Logged by</th>
                  <th style={th} aria-label="Actions" />
                </tr>
              </thead>
              <tbody>
                {monthRows.map((r, i) => {
                  const first = i === 0 || monthRows[i - 1].waste_date !== r.waste_date;
                  const dayRows = monthRows.filter((x) => x.waste_date === r.waste_date);
                  const dayTotal = dayRows.reduce((s, x) => s + x.amount, 0);
                  return (
                    <tr key={r.id} style={form.id === r.id ? { background: '#fdf1ec' } : undefined}>
                      <td style={{ ...td, whiteSpace: 'nowrap', fontWeight: 600, color: 'var(--navy)', borderTop: first && i ? '1px solid var(--gray-300)' : undefined }}>
                        {first ? dayLabel(r.waste_date) : ''}
                        {first && dayRows.length > 1 ? (
                          <>
                            <div style={{ fontWeight: 400, color: 'var(--gray-600)', fontSize: 11 }}>{money(dayTotal)} that day</div>
                            <a href={mailtoForDay(dayRows)} title="One email with everything thrown out this day" style={{ display: 'inline-flex', alignItems: 'center', gap: 4, marginTop: 4, fontSize: 11, fontWeight: 600, color: 'var(--orange)', textDecoration: 'none' }}>
                              <Mail size={12} />Email the whole day
                            </a>
                          </>
                        ) : null}
                      </td>
                      <td style={{ ...td, fontWeight: 600 }}>{r.product}</td>
                      <td style={{ ...td, ...num }}>{qtyText(r)}</td>
                      <td style={{ ...td, ...num }}>{r.unit_cost == null ? '' : `${money(r.unit_cost)}/${r.unit || 'ea'}`}</td>
                      <td style={{ ...td, ...num, fontWeight: 700 }}>{money(r.amount)}</td>
                      <td style={td}>
                        <span style={{ display: 'inline-block', padding: '2px 8px', borderRadius: 10, fontSize: 11, fontWeight: 600, color: 'white', background: REASON_COLOR[r.reason] || REASON_COLOR.Other, whiteSpace: 'nowrap' }}>
                          {r.reason}
                        </span>
                      </td>
                      <td style={{ ...td, color: 'var(--gray-700)', maxWidth: 320 }}>{r.notes}</td>
                      <td style={{ ...td, color: 'var(--gray-700)', whiteSpace: 'nowrap' }}>
                        {r.created_by}
                        {r.source === 'spreadsheet' ? <div style={{ fontSize: 11, color: 'var(--gray-600)' }}>from the spreadsheet</div> : null}
                      </td>
                      <td style={{ ...td, whiteSpace: 'nowrap' }}>
                        <a className="btn btn-ghost" href={mailtoFor(r)} title="Email this entry to Greg, Matt Sr. and Matt Jr." aria-label={`Email ${r.product}`}><Mail size={14} /></a>
                        <button type="button" className="btn btn-ghost" onClick={() => edit(r)} title="Edit" aria-label={`Edit ${r.product}`}><Pencil size={14} /></button>
                        <button type="button" className="btn btn-ghost" onClick={() => remove(r)} title="Remove" aria-label={`Remove ${r.product}`}><Trash2 size={14} /></button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr>
                  <td style={{ ...td, fontWeight: 700 }} colSpan={4}>Total</td>
                  <td style={{ ...td, ...num, fontWeight: 700, color: 'var(--navy)', fontSize: 13 }}>{money(monthTotal)}</td>
                  <td style={td} colSpan={4} />
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </div>

      {/* ── What the comment column could never show ──────────────────────── */}
      <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'flex-start' }}>
        <Bars title={`${year} by product`} rows={byProduct} total={ytd} />
        <Bars title={`${year} by reason`} rows={byReason} total={ytd} colorFor={(l) => REASON_COLOR[l] || REASON_COLOR.Other} />
      </div>
    </div>
  );
}
